import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Company, CompanyFeaturesEnum, features } from '@employ-engage/data';
import ErrorMessage from '../error-message/error-message';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../contexts/auth-context-type';
import { useMutation } from '@apollo/client';
import { CREATE_COMPANY, UPDATE_COMPANY } from '../api';
import { Button } from '../button/button';
import { customAlphabet } from 'nanoid';
import startCase from 'lodash.startcase';
import toLower from 'lodash.tolower';

const nanoid = customAlphabet(
  '1234567890ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  20
);

export interface AddCompanyFormProps {
  isSuperAdminPanel?: boolean;
  setOpen: (open: boolean) => void;
  companySelectedForEditing?: Company | null;
  setCompanySelectedForEditing: (company: Company | null) => void;
  refetchCompaniesUserBelongsTo?: () => void;
  isFirstTimeSetup?: boolean;
}

export function AddCompanyForm({
  isSuperAdminPanel,
  setOpen,
  companySelectedForEditing,
  setCompanySelectedForEditing,
  refetchCompaniesUserBelongsTo,
  isFirstTimeSetup,
}: AddCompanyFormProps) {
  const { user } = useAuthUser();
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const { t } = useTranslation(['common']);
  const [createCompany] = useMutation(CREATE_COMPANY);
  const [updateCompany] = useMutation(UPDATE_COMPANY);

  const defaultFeatureSelections = features.map((feature) => {
    switch (feature.id) {
      case CompanyFeaturesEnum.PREMIUM_DATA_VISUALIZATIONS:
        return {
          ...feature,
          isEnabled: false,
        };
      default:
        return {
          ...feature,
          isEnabled: false,
        };
    }
  });

  useEffect(() => {
    if (inputRef) {
      inputRef.focus();
    }
  }, [inputRef]);

  const generateFriendlyCompanyId = () => {
    return nanoid();
  };

  const initialCompanyId = generateFriendlyCompanyId();

  const {
    handleSubmit,
    handleChange,
    resetForm,
    values,
    errors,
    touched,
    setErrors,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: companySelectedForEditing
      ? (companySelectedForEditing as Company)
      : {
          friendlyCompanyId: initialCompanyId,
          name: startCase(toLower(initialCompanyId.replace(/-/g, ' '))),
          pricePerEmployee: 0,
          pricePerEmailedEmployee: 0,
          employeePointOfContact: {
            name: '',
            email: '',
          },
          featureSelections: defaultFeatureSelections,
        },
    onSubmit: async (values) => {
      try {
        if (companySelectedForEditing) {
          await updateCompany({
            variables: {
              id: companySelectedForEditing.id,
              input: {
                friendlyCompanyId: values.friendlyCompanyId,
                name: values.name,
              },
            },
          });
        } else {
          await createCompany({
            variables: {
              input: {
                id: values.friendlyCompanyId,
                friendlyCompanyId: values.friendlyCompanyId,
                name: values.name,
              },
            },
          });
        }
        refetchCompaniesUserBelongsTo?.();
        resetForm();
        setCompanySelectedForEditing(null);
        setOpen(false);
      } catch (error: any) {
        console.error('Error creating/updating company:', error);
        if (error.message.includes('duplicate key error')) {
          setErrors({
            friendlyCompanyId: t('Company ID already exists'),
          });
        } else {
          setErrors({
            friendlyCompanyId: t(
              'An error occurred while saving the company: {{error}}',
              { error: error.message }
            ),
          });
        }
      }
    },
    validationSchema: Yup.object({
      friendlyCompanyId: Yup.string()
        .required('Required')
        .matches(
          /^[a-z0-9]+(-[a-z0-9]+)*$/,
          'Must be lowercase, numbers, and hyphens only'
        ),
      name: Yup.string().required('Required'),
    }),
  });

  useEffect(() => {
    if (user?.email && user?.displayName) {
      setFieldValue('employeePointOfContact', {
        name: user.displayName,
        email: user.email,
      });
    }
  }, [user]);

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5">
      <div className="grid grid-cols-2 gap-3.5">
        <div className="col-span-2">
          <label
            htmlFor="friendlyCompanyId"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Company ID')}
            <span className="text-xs text-gray-500 ml-1">
              {t(
                'This ID will be used to identify your company. It will be used in URLs and should be unique.'
              )}
            </span>
          </label>
          <div className="flex items-start space-x-2">
            <div className="flex-1">
              <input
                ref={setInputRef}
                type="text"
                name="friendlyCompanyId"
                id="friendlyCompanyId"
                autoComplete="friendly-company-id"
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                onChange={(e) => {
                  setFieldValue('friendlyCompanyId', e.target.value);
                }}
                value={values.friendlyCompanyId}
                disabled
              />
              <ErrorMessage
                errors={errors.friendlyCompanyId}
                touched={touched.friendlyCompanyId}
              />
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Company Name')}
          </label>
          <input
            type="text"
            name="name"
            id="name"
            autoComplete="given-name"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={(e) => {
              setValues({
                ...values,
                name: e.target.value,
              });
            }}
            value={values.name}
          />
          <ErrorMessage errors={errors.name} touched={touched.name} />
        </div>
      </div>
      {isSuperAdminPanel && (
        <div className="grid grid-cols-2 gap-3.5">
          <div className="col-span-1">
            <label
              htmlFor="pricePerEmployee"
              className="block text-sm font-medium text-gray-700"
            >
              {t('Price Per Employee')}
            </label>
            <input
              type="number"
              name="pricePerEmployee"
              id="pricePerEmployee"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={handleChange}
              value={values.pricePerEmployee}
            />
            <ErrorMessage
              errors={errors.pricePerEmployee}
              touched={touched.pricePerEmailedEmployee}
            />
          </div>
          <div className="col-span-1">
            <label
              htmlFor="pricePerEmailedEmployee"
              className="block text-sm font-medium text-gray-700"
            >
              {t('Price Per Emailed Employee')}
            </label>
            <input
              type="number"
              name="pricePerEmailedEmployee"
              id="pricePerEmailedEmployee"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={handleChange}
              value={values.pricePerEmailedEmployee}
            />
            <ErrorMessage
              errors={errors.pricePerEmailedEmployee}
              touched={touched.pricePerEmailedEmployee}
            />
          </div>
        </div>
      )}
      {/* <div className="grid grid-cols-2 gap-3.5">
        <div className="col-span-1">
          <label
            htmlFor="employeePointOfContact.name"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Employee POC Name')}
          </label>
          <input
            type="text"
            name="employeePointOfContact.name"
            id="employeePointOfContact.name"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={handleChange}
            value={values.employeePointOfContact?.name}
          />
          <ErrorMessage
            errors={errors.employeePointOfContact}
            touched={touched.employeePointOfContact}
          />
        </div>
        <div className="col-span-1">
          <label
            htmlFor="employeePointOfContact.email"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Employee POC Email')}
          </label>
          <input
            type="email"
            name="employeePointOfContact.email"
            id="employeePointOfContact.email"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={handleChange}
            value={values.employeePointOfContact?.email}
          />
          <ErrorMessage
            errors={errors.employeePointOfContact}
            touched={touched.employeePointOfContact}
          />
        </div>
      </div> */}
      <div className="flex justify-end mt-5">
        {!isFirstTimeSetup && (
          <Button
            type="button"
            variant="secondary"
            className="px-4 py-2 mr-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => {
              resetForm();
              setCompanySelectedForEditing(null);
              setOpen(false);
            }}
          >
            {t('Cancel')}
          </Button>
        )}
        <Button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {t('Save')}
        </Button>
      </div>
    </form>
  );
}

export default AddCompanyForm;
